import React, { useContext, useRef, useState, useEffect } from 'react';
import Button from "../../components/atoms/Button";
import ButtonGroup from "../../components/molecules/ButtonGroup";
import InputGroup from "../../components/molecules/InputGroup";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateRight } from "@fortawesome/free-solid-svg-icons";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import moment from 'moment-timezone';

const OfficeHeaderNav = ({
    locationData,
    loading,
    isToday,
    onRefresh,
    selectedDate,
    setSelectedDate,
    minDate,
    maxDate,
    enabledDates,
    showCalendar,
    setShowCalendar,
    viewMode,
    setViewMode,
}) => {
    const calendarRef = useRef(null);

    const toDate = (date) => {
        if (!date) return null;
        if (date instanceof Date) return date;
        if (typeof date === 'string') {
            const [y, m, d] = date.split('-').map(Number);
            return new Date(y, m - 1, d);
        }
        return new Date(date);
    };

    const minDateObj = toDate(minDate);
    const maxDateObj = toDate(maxDate);
    const enabledDatesArray = enabledDates?.map(toDate) || [];

    const handleCalendarToggle = () => {
        setShowCalendar(!showCalendar);
    };

    const handleDateSelect = (date) => {
        let clampedDate = date;
        if (minDateObj && date < minDateObj) {
            clampedDate = minDateObj;
        } else if (maxDateObj && date > maxDateObj) {
            clampedDate = maxDateObj;
        }
        setSelectedDate(date);
        setShowCalendar(false);
    };

    const tileDisabled = ({ date, view }) => {
        if (view !== 'month') return false;
        if (!enabledDatesArray || enabledDatesArray.length === 0) return false;
        const dateString = moment(date).format('YYYY-MM-DD');
        return !enabledDatesArray.some(enabledDate =>
            moment(enabledDate).format('YYYY-MM-DD') === dateString
        );
    };

    const handleClickOutside = (event) => {
        if (calendarRef.current && !calendarRef.current.contains(event.target)) {
            setShowCalendar(false);
        }
    };

    useEffect(() => {
        if (showCalendar) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showCalendar]);

    const displayDate = selectedDate ? moment(selectedDate).format("l") : moment().format("l");
    const isSelectedToday = isToday;
    const isOverview = viewMode === 'overview';

    const getDefaultActiveStartDate = () => {
        if (selectedDate) {
            return new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
        }
        if (minDateObj) {
            return new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1);
        }
        return new Date();
    };

    const getNavigationLabel = ({ date }) => {
        if (minDateObj && maxDateObj) {
            const minMonth = minDateObj.getMonth();
            const maxMonth = maxDateObj.getMonth();
            const minYear = minDateObj.getFullYear();
            const maxYear = maxDateObj.getFullYear();
            if (minMonth === maxMonth && minYear === maxYear) {
                return `${moment(minDateObj).format('MMMM YYYY')}`;
            }
        }
        return moment(date).format('MMMM YYYY');
    };

    const shouldDisableNavigation = () => {
        if (minDateObj && maxDateObj) {
            const minMonth = minDateObj.getMonth();
            const maxMonth = maxDateObj.getMonth();
            const minYear = minDateObj.getFullYear();
            const maxYear = maxDateObj.getFullYear();
            return minMonth === maxMonth && minYear === maxYear;
        }
        return false;
    };

    return (
        <div className="between" style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <img
                    style={{ marginRight: "24px", transform: 'scale(1.1)' }}
                    width={80}
                    height={80}
                    src={require(`../../clients/${locationData.logoPath}`)}
                    alt="logo"
                />
                <span className="my-auto" style={{ fontSize: "2.5rem" }}>
                    {locationData.name}
                </span>
            </div>

            <div className="my-auto">
                <InputGroup>
                    {isSelectedToday && !isOverview && (
                        <div className="d-flex align-items-center">
                            <span style={{ color: "red", marginRight: "12px", fontSize: "1.25rem" }}>
                                LIVE
                            </span>
                            <span className="pulsing-dot" style={{ marginRight: "16px" }}></span>
                        </div>
                    )}
                    <ButtonGroup className="me-1">
                        <Button
                            className="panel-2 btn"
                            icon="calendar"
                            text={displayDate + (isSelectedToday ? " (Today)" : "")}
                            onClick={handleCalendarToggle}
                            disabled={loading || isOverview}
                        />

                        <Button
                            icon={"sun-fill"}
                            text="Regular Day"
                            onClick={() => setViewMode('regular')}
                            className={`panel-2 ${!isOverview ? 'active' : ''}`}
                            disabled={loading}
                        />

                        <Button
                            icon={"bar-chart-fill"}
                            text="Overview"
                            onClick={() => setViewMode('overview')}
                            className={`panel-2 ${isOverview ? 'active' : ''}`}
                            disabled={loading}
                        />
                    </ButtonGroup>
                    <ButtonGroup>
                        {isSelectedToday && !isOverview && (
                            <Button
                                className="border-0"
                                onClick={onRefresh}
                                disabled={loading}>
                                {!loading && (
                                    <div className="d-flex align-items-center">
                                        <FontAwesomeIcon icon={faArrowRotateRight} />
                                        <span style={{ marginLeft: "8px" }}>Refresh</span>
                                    </div>
                                )}
                            </Button>
                        )}
                    </ButtonGroup>
                </InputGroup>
            </div>

            {showCalendar && !isOverview && (
                <div
                    ref={calendarRef}
                    style={{
                        position: 'absolute', top: '100%', right: '5%',
                        transform: 'translateX(-50%)', zIndex: 1000,
                        animation: 'fadeIn 0.2s ease-in-out',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        background: 'white', borderRadius: '8px',
                    }}
                >
                    <div className="calendar-container">
                        <Calendar
                            onChange={handleDateSelect}
                            value={selectedDate}
                            minDate={minDateObj}
                            maxDate={maxDateObj}
                            defaultActiveStartDate={getDefaultActiveStartDate()}
                            minDetail="month"
                            maxDetail="month"
                            showNeighboringMonth={false}
                            navigationLabel={getNavigationLabel}
                            prev2Label={null}
                            next2Label={null}
                            prevLabel={shouldDisableNavigation() ? null : undefined}
                            nextLabel={shouldDisableNavigation() ? null : undefined}
                            tileDisabled={tileDisabled}
                        />
                    </div>
                </div>
            )}

            <style>
                {`
                    .pulsing-dot {
                        width: 12px; height: 12px; background-color: red;
                        border-radius: 50%; animation: pulse 2s infinite;
                    }
                    @keyframes pulse {
                        0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7); }
                        70% { transform: scale(1); box-shadow: 0 0 0 0 6px rgba(255, 0, 0, 0); }
                        100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 0, 0, 0); }
                    }
                    @keyframes fadeIn {
                        from { opacity: 0; transform: translateX(-50%) translateY(-10px); }
                        to { opacity: 1; transform: translateX(-50%) translateY(0); }
                    }
                    .react-calendar__tile--active { background: #53b8aa !important; color: black !important; }
                    .react-calendar__tile--active:enabled:hover,
                    .react-calendar__tile--active:enabled:focus { background: #53b8aa !important; }
                    .react-calendar__tile--now { background: #9fdbd2 !important; }
                    .react-calendar__tile--now:enabled:hover,
                    .react-calendar__tile--now:enabled:focus { background: #9fdbd2 !important; }
                    .react-calendar__tile:disabled {
                        background-color: #f0f0f0 !important; color: #ccc !important; cursor: not-allowed !important;
                    }
                    .react-calendar__navigation button { display: ${shouldDisableNavigation() ? 'none' : 'flex'}; }
                    .react-calendar__navigation__label {
                        display: flex !important; pointer-events: none;
                        background: transparent !important; font-weight: bold;
                    }
                `}
            </style>
        </div>
    );
};

export default OfficeHeaderNav;